import { Modal, Button } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useMemo } from "react";

import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import ConfirmDialog from "../ConfirmDialogue";
import ModalLoader from "../utils/ModalLoader";
import CloseIcon from "mdi-react/CloseIcon";
import { OutputSettingsSections } from "./OutputSettingSection";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as yup from "yup";

const defaultItem = {
  BOM: "",
  unit: "",
  period: "",
  periodTime: "",
  unitValue: "",
  name: "",
  plant: "",
  stopAtTarget: false,
  fromConversion: false,
};

const validationSchema = yup.object({
  items: yup.array().of(
    yup.object({
      BOM: yup.string().required("BOM is required"),
      unit: yup.string().required("Unit is required"),
      period: yup.string().required("Period is required"),
      periodTime: yup.string().required("Period Time is required"),
      unitValue: yup.string().required("Unit Value is required"),
    }),
  ),
});

const OutputSettingsModal = ({
  refetch,
  onHide,
  singlePlant,
  show,
  equipment,
}) => {
  const { backendUrl } = useAuth();

  const plantId = singlePlant.plantId;

  const fetchSavedSettings = async () => {
    const response = await fetchActionsUtil(
      `${backendUrl}/api/production/output-settings?plant=${plantId}&serialNumber=${equipment}`,
      "GET",
    );

    return response;
  };

  const { data } = useQuery(
    ["OUTPUT_SETTING", { plantId, equipment }],
    () => fetchSavedSettings(),
    {
      keepPreviousData: false,
      staleTime: 0,
    },
  );

  const outputSettings = useMemo(() => data?.data || [], [data]);

  const fetchBOM = async () => {
    const response = await fetchActionsUtil(
      `${backendUrl}/api/production/output-bills-of-materials?plant=${plantId}`,
      "GET",
    );

    return response;
  };

  const { data: BOMItems = [], refetch: reFreshBill } = useQuery(
    ["OUTPUT_BILL_OF_QUANTITY", { plantId }],
    () => fetchBOM(plantId),
    {
      keepPreviousData: false,
      staleTime: 0,
    },
  );

  const BOM = useMemo(() => {
    if (!BOMItems) return [];
    return BOMItems?.map((el) => ({
      value: el.Bar_Code ?? String(el?.id),
      label: el.Item_Name ?? el?.templateName,
      fromConversion: el?.templateName ? true : false,
    }));
  }, [BOMItems]);

  const initialValues = useMemo(() => {
    if (outputSettings?.length > 0) {
      return {
        items: [...outputSettings],
      };
    }
    return {
      items: [
        {
          ...defaultItem,
        },
      ],
    };
  }, [outputSettings]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const newValues = values.items.map((el) => ({ ...el, plant: plantId }));
      // console.log({ newValues });

      if (
        await ConfirmDialog({
          title: "Save Production Setting",
          description: "Are you sure you want to save this Setting",
        })
      ) {
        outputSettingsMutation.mutate(newValues);
      }
    },
  });

  const outputSettingsMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/production/output-settings`,
        "POST",
        "",
        { items: payload, equipment },
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(data);

        refetch();
        onHide();
      },
      onError: ({ error }) => {
        toast.error("Unable to save output settings");
      },
    },
  );

  // console.log("FORMIK =>", formik.values.items, outputSettings);

  return (
    <Modal
      onHide={onHide}
      show={show}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body>
        <div>
          <div>
            <div className="d-flex justify-content-between">
              <h6>{singlePlant?.plantName.toUpperCase()}</h6>
              <div role="button" className="text-lg" onClick={onHide}>
                <CloseIcon />
              </div>
            </div>
            <p className="text-muted">
              You can add Periodic output settings for this{" "}
              {equipment ? "equipment" : "plant"}
            </p>
          </div>
          {/*  */}
          <div className="container mt-3">
            {/*  */}
            <div className="mb-3">
              <FormikProvider value={formik}>
                <form noValidate className="form-relative">
                  {/* */}
                  <FieldArray
                    name="items"
                    render={(arrayHelpers) => (
                      <>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(min(400px, 100%), 1fr))",
                            gap: "30px",
                          }}
                        >
                          {formik.values.items.map((item, index) => (
                            <OutputSettingsSections
                              key={item.id ?? index}
                              item={item}
                              index={index}
                              formik={formik}
                              arrayHelpers={arrayHelpers}
                              isFirst={index === 0}
                              BOM={BOM}
                              reFreshBill={reFreshBill}
                            />
                          ))}
                        </div>

                        <div className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            onClick={() =>
                              arrayHelpers.push({
                                ...defaultItem,
                              })
                            }
                          >
                            Add Another Output
                          </Button>
                        </div>
                      </>
                    )}
                  />
                </form>
              </FormikProvider>
            </div>
          </div>

          {/*  */}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between m-4">
          <button className="btn bg print mx-4" onClick={onHide}>
            Cancel
          </button>
          <Button variant="outline-primary" onClick={formik.handleSubmit}>
            {outputSettingsMutation.isLoading
              ? "Sending..."
              : `${
                  outputSettings?.length > 0 ? "Update" : "Save"
                } Output Settings`}
          </Button>
        </div>
      </Modal.Footer>
      <ModalLoader show={outputSettingsMutation.isLoading} />
    </Modal>
  );
};

export default OutputSettingsModal;
